
import {
  ChangeDetectorRef,
  Component,
  OnInit,
  inject
} from '@angular/core';

import {
  CommonModule
} from '@angular/common';

import {
  FormsModule
} from '@angular/forms';

import {
  PacoteAdicional,
  PacoteAdicionalRequest,
  PacoteAdicionalService
} from '../../../service/pacote-adicional.service';

import Swal from 'sweetalert2';


@Component({
  selector: 'app-pacotes',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule
  ],
  templateUrl: './pacotes.component.html',
  styleUrl: './pacotes.component.css'
})
export class PacotesComponent implements OnInit {

  private readonly pacoteService =
    inject(PacoteAdicionalService);

  private readonly cdr =
    inject(ChangeDetectorRef);


  // ============================================================
  // LISTAGEM
  // ============================================================

  pacotes: PacoteAdicional[] = [];

  carregando = false;

  salvando = false;


  // ============================================================
  // FORMULÁRIO
  // ============================================================

  mostrarFormulario = false;

  editando = false;

  pacoteSelecionado: PacoteAdicional | null = null;


  // ============================================================
  // MENSAGENS
  // ============================================================

  mensagemSucesso = '';

  mensagemErro = '';


  // ============================================================
  // IMAGENS
  // ============================================================

  imagensSelecionadas: File[] = [];

  previews: string[] = [];


  // ============================================================
  // FORM
  // ============================================================

  form: PacoteAdicionalRequest = {

    nome: '',

    descricao: '',

    itensInclusos: '',

    quartoId: 1,

    valor: 0,

    ativo: true
  };


  // ============================================================
  // INIT
  // ============================================================

  ngOnInit(): void {

    this.carregarPacotes();
  }


  // ============================================================
  // CARREGAR PACOTES
  // ============================================================

  carregarPacotes(): void {

    this.carregando = true;

    this.cdr.detectChanges();

    this.pacoteService
      .listarTodos()
      .subscribe({

        next: (pacotes) => {

          this.pacotes = pacotes;

          this.carregando = false;

          this.cdr.detectChanges();
        },

        error: (erro) => {

          console.error(
            'Erro ao carregar pacotes:',
            erro
          );

          this.carregando = false;

          this.cdr.detectChanges();

          this.mostrarErro(
            this.obterMensagemErro(
              erro,
              'Não foi possível carregar os pacotes.'
            )
          );
        }
      });
  }


  // ============================================================
  // NOVO PACOTE
  // ============================================================

  novoPacote(): void {

    this.editando = false;

    this.pacoteSelecionado = null;

    this.form = {

      nome: '',

      descricao: '',

      itensInclusos: '',

      quartoId: 1,

      valor: 0,

      ativo: true
    };

    this.limparImagens();

    this.limparMensagens();

    this.mostrarFormulario = true;

    this.cdr.detectChanges();
  }


  // ============================================================
  // EDITAR PACOTE
  // ============================================================

  editarPacote(
    pacote: PacoteAdicional
  ): void {

    this.editando = true;

    this.pacoteSelecionado = pacote;

    this.form = {

      nome: pacote.nome,

      descricao:
        pacote.descricao ?? '',

      itensInclusos:
        pacote.itensInclusos ?? '',

      quartoId:
        pacote.quartoId,

      valor:
        pacote.valor,

      ativo:
        pacote.ativo
    };

    this.limparImagens();

    this.limparMensagens();

    this.mostrarFormulario = true;

    this.cdr.detectChanges();
  }


  // ============================================================
  // FECHAR FORMULÁRIO
  // ============================================================

  fecharFormulario(): void {

    if (this.salvando) {

      return;
    }

    this.mostrarFormulario = false;

    this.pacoteSelecionado = null;

    this.limparImagens();

    this.cdr.detectChanges();
  }


  // ============================================================
  // SALVAR
  // ============================================================

  salvar(): void {

    this.limparMensagens();

    if (!this.validarFormulario()) {

      return;
    }

    this.salvando = true;

    this.cdr.detectChanges();


    const dados: PacoteAdicionalRequest = {

      nome:
        this.form.nome.trim(),

      descricao:
        this.form.descricao?.trim() || null,

      itensInclusos:
        this.form.itensInclusos?.trim() || null,

      quartoId:
        Number(this.form.quartoId),

      valor:
        Number(this.form.valor),

      ativo:
        this.form.ativo
    };


    if (
      this.editando &&
      this.pacoteSelecionado
    ) {

      this.atualizarPacote(
        this.pacoteSelecionado.id,
        dados
      );

    } else {

      this.criarPacote(dados);
    }
  }


  // ============================================================
  // CRIAR PACOTE
  // ============================================================

  private criarPacote(
    dados: PacoteAdicionalRequest
  ): void {

    this.pacoteService
      .criar(dados)
      .subscribe({

        next: (pacote) => {

          this.salvarImagens(
            pacote,
            true
          );
        },

        error: (erro) => {

          console.error(
            'Erro ao criar pacote:',
            erro
          );

          this.salvando = false;

          this.cdr.detectChanges();

          this.mostrarErro(
            this.obterMensagemErro(
              erro,
              'Não foi possível cadastrar o pacote.'
            )
          );
        }
      });
  }


  // ============================================================
  // ATUALIZAR PACOTE
  // ============================================================

  private atualizarPacote(
    id: number,
    dados: PacoteAdicionalRequest
  ): void {

    this.pacoteService
      .atualizar(
        id,
        dados
      )
      .subscribe({

        next: (pacote) => {

          this.salvarImagens(
            pacote,
            false
          );
        },

        error: (erro) => {

          console.error(
            'Erro ao atualizar pacote:',
            erro
          );

          this.salvando = false;

          this.cdr.detectChanges();

          this.mostrarErro(
            this.obterMensagemErro(
              erro,
              'Não foi possível atualizar o pacote.'
            )
          );
        }
      });
  }


  // ============================================================
  // SALVAR IMAGENS
  // ============================================================

  private salvarImagens(
    pacote: PacoteAdicional,
    novo: boolean
  ): void {

    /*
     * Se não houver imagens selecionadas,
     * finaliza imediatamente.
     */

    if (
      this.imagensSelecionadas.length === 0
    ) {

      this.finalizarSalvamento(

        novo
          ? 'Pacote cadastrado com sucesso!'
          : 'Pacote atualizado com sucesso!'
      );

      return;
    }


    this.pacoteService
      .adicionarImagens(
        pacote.id,
        this.imagensSelecionadas
      )
      .subscribe({

        next: () => {

          this.finalizarSalvamento(

            novo
              ? 'Pacote cadastrado com sucesso!'
              : 'Pacote atualizado com sucesso!'
          );
        },

        error: (erro) => {

          console.error(
            'Erro ao enviar imagens:',
            erro
          );

          this.salvando = false;

          this.cdr.detectChanges();


          Swal.fire({

            icon: 'warning',

            title: 'Pacote salvo!',

            text:
              'O pacote foi salvo, mas não foi possível enviar uma ou mais imagens.',

            confirmButtonText: 'OK',

            confirmButtonColor: '#696A49'
          });


          this.carregarPacotes();
        }
      });
  }


  // ============================================================
  // FINALIZAR SALVAMENTO
  // ============================================================

  private finalizarSalvamento(
    mensagem: string
  ): void {

    this.salvando = false;

    this.mostrarFormulario = false;

    this.pacoteSelecionado = null;

    this.limparImagens();

    this.cdr.detectChanges();


    this.carregarPacotes();


    this.mostrarSucesso(
      mensagem
    );
  }


  // ============================================================
  // SELECIONAR IMAGENS
  // ============================================================

  selecionarImagens(
    event: Event
  ): void {

    const input =
      event.target as HTMLInputElement;


    if (!input.files) {

      return;
    }


    const arquivos =
      Array.from(input.files);


    const imagensValidas: File[] = [];


    for (
      const arquivo of arquivos
    ) {

      /*
       * Verifica se é imagem
       */

      if (
        !arquivo.type.startsWith('image/')
      ) {

        this.mostrarErro(

          `O arquivo "${arquivo.name}" não é uma imagem.`
        );

        continue;
      }


      /*
       * Limite de 5 MB
       */

      if (
        arquivo.size >
        5 * 1024 * 1024
      ) {

        this.mostrarErro(

          `A imagem "${arquivo.name}" excede o limite de 5 MB.`
        );

        continue;
      }


      imagensValidas.push(
        arquivo
      );
    }


    this.imagensSelecionadas.push(
      ...imagensValidas
    );


    this.criarPreviews();


    /*
     * Permite selecionar novamente
     * o mesmo arquivo.
     */

    input.value = '';


    this.cdr.detectChanges();
  }


  // ============================================================
  // REMOVER IMAGEM SELECIONADA
  // ============================================================

  removerImagemSelecionada(
    index: number
  ): void {

    if (
      index < 0 ||
      index >= this.imagensSelecionadas.length
    ) {

      return;
    }


    this.imagensSelecionadas.splice(
      index,
      1
    );


    this.criarPreviews();

    this.cdr.detectChanges();
  }


  // ============================================================
  // CRIAR PREVIEWS
  // ============================================================

  private criarPreviews(): void {

    /*
     * Libera os objetos anteriores
     * para evitar vazamento de memória.
     */

    this.previews.forEach(
      url =>
        URL.revokeObjectURL(url)
    );


    this.previews =
      this.imagensSelecionadas.map(
        arquivo =>
          URL.createObjectURL(arquivo)
      );
  }


  // ============================================================
  // EXCLUIR IMAGEM EXISTENTE
  // ============================================================

  excluirImagem(
    imagemId: number
  ): void {

    if (
      !this.pacoteSelecionado
    ) {

      return;
    }


    Swal.fire({

      icon: 'warning',

      title: 'Excluir imagem?',

      text:
        'Esta ação não poderá ser desfeita.',

      showCancelButton: true,

      confirmButtonText:
        'Sim, excluir',

      cancelButtonText:
        'Cancelar',

      confirmButtonColor:
        '#d33',

      cancelButtonColor:
        '#696A49'
    })
    .then((resultado) => {

      if (
        !resultado.isConfirmed
      ) {

        return;
      }


      this.pacoteService
        .excluirImagem(
          this.pacoteSelecionado!.id,
          imagemId
        )
        .subscribe({

          next: (pacote) => {

            this.pacoteSelecionado =
              pacote;


            /*
             * Atualiza a lista
             */

            this.carregarPacotes();


            this.cdr.detectChanges();


            this.mostrarSucesso(
              'Imagem excluída com sucesso.'
            );
          },

          error: (erro) => {

            console.error(
              'Erro ao excluir imagem:',
              erro
            );


            this.cdr.detectChanges();


            this.mostrarErro(
              this.obterMensagemErro(
                erro,
                'Não foi possível excluir a imagem.'
              )
            );
          }
        });
    });
  }


  // ============================================================
  // ALTERAR STATUS
  // ============================================================

  alterarStatus(
    pacote: PacoteAdicional
  ): void {

    const novoStatus =
      !pacote.ativo;


    this.pacoteService
      .alterarStatus(
        pacote.id,
        novoStatus
      )
      .subscribe({

        next: (atualizado) => {

          const index =
            this.pacotes.findIndex(
              item =>
                item.id === atualizado.id
            );


          if (
            index !== -1
          ) {

            this.pacotes[index] =
              atualizado;
          }


          this.cdr.detectChanges();


          this.mostrarSucesso(

            atualizado.ativo

              ? 'Pacote ativado com sucesso.'

              : 'Pacote desativado com sucesso.'
          );
        },

        error: (erro) => {

          console.error(
            'Erro ao alterar status:',
            erro
          );


          this.cdr.detectChanges();


          this.mostrarErro(
            this.obterMensagemErro(
              erro,
              'Não foi possível alterar o status do pacote.'
            )
          );
        }
      });
  }


  // ============================================================
  // EXCLUIR PACOTE
  // ============================================================

  excluirPacote(
    pacote: PacoteAdicional
  ): void {

    Swal.fire({

      icon: 'warning',

      title: 'Excluir pacote?',

      html:
        `Deseja realmente excluir o pacote <strong>"${pacote.nome}"</strong>?`,

showCancelButton: true,

  confirmButtonText:
'Sim, excluir',

  cancelButtonText:
'Cancelar',

  confirmButtonColor:
'#d33',

  cancelButtonColor:
'#696A49',

  reverseButtons: true
})
.then((resultado) => {

  if (
    !resultado.isConfirmed
  ) {

    return;
  }


  /*
   * Mostra loading enquanto exclui
   */

  Swal.fire({

    title: 'Excluindo...',

    text:
      'Aguarde enquanto o pacote é removido.',

    allowOutsideClick: false,

    allowEscapeKey: false,

    didOpen: () => {

      Swal.showLoading();
    }
  });


  this.pacoteService
    .excluir(
      pacote.id
    )
    .subscribe({

      next: () => {

        this.pacotes =
          this.pacotes.filter(
            item =>
              item.id !== pacote.id
          );


        this.cdr.detectChanges();


        Swal.close();


        this.mostrarSucesso(
          'Pacote excluído com sucesso.'
        );
      },

      error: (erro) => {

        console.error(
          'Erro ao excluir pacote:',
          erro
        );


        this.cdr.detectChanges();


        Swal.close();


        this.mostrarErro(
          this.obterMensagemErro(
            erro,
            'Não foi possível excluir o pacote.'
          )
        );
      }
    });
});
}


// ============================================================
// VALIDAÇÃO
// ============================================================

validarFormulario(): boolean {

  /*
   * Nome
   */

  if (
    !this.form.nome ||
    !this.form.nome.trim()
  ) {

    this.mostrarErro(
      'Informe o nome do pacote.'
    );

    return false;
  }


  /*
   * Acomodação
   */

  if (
    !this.form.quartoId
  ) {

    this.mostrarErro(
      'Selecione a acomodação.'
    );

    return false;
  }


  /*
   * Valor
   */

  if (
    this.form.valor === null ||
    this.form.valor === undefined ||
    Number(this.form.valor) <= 0
  ) {

    this.mostrarErro(
      'Informe um valor maior que zero.'
    );

    return false;
  }


  return true;
}


// ============================================================
// FORMATAR VALOR
// ============================================================

formatarValor(
  valor: number
): string {

  return new Intl.NumberFormat(
    'pt-BR',
    {
      style: 'currency',
      currency: 'BRL'
    }
  ).format(valor);
}


// ============================================================
// URL DA IMAGEM
// ============================================================

obterImagemUrl(
  url: string
): string {

  return this.pacoteService
    .getImagemUrl(url);
}


// ============================================================
// ITENS INCLUSOS
// ============================================================

getItens(
  itens: string | null
): string[] {

  if (!itens) {

    return [];
  }


  return itens
    .split('\n')
    .map(
      item =>
        item.trim()
    )
    .filter(
      item =>
        item.length > 0
    );
}


// ============================================================
// LIMPAR IMAGENS
// ============================================================

private limparImagens(): void {

  /*
   * Libera os Object URLs
   */

  this.previews.forEach(
    url =>
      URL.revokeObjectURL(url)
  );


  this.imagensSelecionadas = [];

  this.previews = [];


  this.cdr.detectChanges();
}


// ============================================================
// LIMPAR MENSAGENS
// ============================================================

private limparMensagens(): void {

  this.mensagemErro = '';

  this.mensagemSucesso = '';

  this.cdr.detectChanges();
}


// ============================================================
// MOSTRAR ERRO
// ============================================================

private mostrarErro(
  mensagem: string
): void {

  this.mensagemErro =
    mensagem;

  this.mensagemSucesso =
    '';


  this.cdr.detectChanges();


  Swal.fire({

    icon: 'error',

    title: 'Ops!',

    text: mensagem,

    confirmButtonText:
      'Entendi',

    confirmButtonColor:
      '#696A49'
  });
}


// ============================================================
// MOSTRAR SUCESSO
// ============================================================

private mostrarSucesso(
  mensagem: string
): void {

  this.mensagemSucesso =
    mensagem;

  this.mensagemErro =
    '';


  this.cdr.detectChanges();


  Swal.fire({

    icon: 'success',

    title: 'Sucesso!',

    text: mensagem,

    confirmButtonText:
      'OK',

    confirmButtonColor:
      '#696A49',

    timer: 2500,

    timerProgressBar: true,

    showConfirmButton: false
  })
    .then(() => {

      this.mensagemSucesso = '';

      this.cdr.detectChanges();
    });
}


// ============================================================
// MENSAGEM DE ERRO DA API
// ============================================================

private obterMensagemErro(
  erro: any,
  fallback: string
): string {

  return (

    erro?.error?.message

    ||

    erro?.error?.mensagem

    ||

    erro?.error?.error

    ||

    (
      typeof erro?.error === 'string'
        ? erro.error
        : null
    )

    ||

    fallback
  );
}
}
